/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type CSSProperties, type FC, type ReactNode } from 'react';

import type { OIDCProvider } from './service';

export const inputStyle: CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  borderRadius: 8,
  border: '1px solid var(--border-2)',
  background: 'var(--bg-card)',
  color: 'var(--text-1)',
  fontSize: 14,
  outline: 'none',
  transition: 'border .12s, box-shadow .12s',
};

export const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-2)',
};

export const primaryButtonStyle = (disabled: boolean): CSSProperties => ({
  marginTop: 22,
  width: '100%',
  height: 42,
  border: 'none',
  borderRadius: 8,
  background: 'var(--brand-500)',
  color: 'white',
  fontWeight: 600,
  fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  boxShadow: '0 1px 0 rgba(0,82,217,.4), 0 4px 12px rgba(0,100,250,.25)',
  transition: 'background .12s',
});

export const FormField: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <div style={{ marginTop: 6 }}>{children}</div>
  </div>
);

export const SsoButton: FC<{
  provider: OIDCProvider;
  full?: boolean;
  onClick?: () => void;
}> = ({ provider, full, onClick }) => (
  <button
    data-testid={`login.button.oidc.${provider.name}`}
    type="button"
    onClick={onClick}
    style={{
      gridColumn: full ? '1 / -1' : 'auto',
      marginTop: full ? 10 : 0,
      height: 40,
      border: '1px solid var(--border-2)',
      borderRadius: 8,
      background: 'var(--bg-card)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontSize: 13,
      fontWeight: 500,
      color: 'var(--text-1)',
      cursor: 'pointer',
      transition: 'background .12s, border-color .12s',
    }}
    onMouseOver={e => {
      e.currentTarget.style.background = 'var(--bg-elev)';
      e.currentTarget.style.borderColor = 'var(--border-3)';
    }}
    onMouseOut={e => {
      e.currentTarget.style.background = 'var(--bg-card)';
      e.currentTarget.style.borderColor = 'var(--border-2)';
    }}
  >
    <SsoIcon name={provider.name} />
    <span>{provider.display_name || provider.name}</span>
  </button>
);

const SsoIcon: FC<{ name: string }> = ({ name }) => {
  const key = name.toLowerCase();
  if (key === 'google') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22 12.2c0-.7 0-1.4-.2-2H12v3.9h5.6c-.2 1.3-1 2.4-2.1 3.1v2.6h3.4c2-1.8 3.1-4.5 3.1-7.6Z"
        />
        <path
          fill="#34A853"
          d="M12 22c2.8 0 5.2-.9 6.9-2.5l-3.4-2.6c-.9.6-2.1 1-3.5 1-2.7 0-5-1.8-5.8-4.2H2.7v2.7C4.4 19.8 8 22 12 22Z"
        />
        <path
          fill="#FBBC05"
          d="M6.2 13.7c-.2-.6-.3-1.2-.3-1.8s.1-1.2.3-1.8V7.4H2.7C2 8.8 1.6 10.3 1.6 12s.4 3.2 1.1 4.6l3.5-2.9Z"
        />
        <path
          fill="#EA4335"
          d="M12 5.8c1.5 0 2.9.5 3.9 1.5l2.9-2.9C17.2 2.9 14.8 2 12 2 8 2 4.4 4.2 2.7 7.4l3.5 2.6C7 7.6 9.3 5.8 12 5.8Z"
        />
      </svg>
    );
  }
  if (key === 'github') {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="#1A1F29"
        aria-hidden="true"
      >
        <path d="M12 .5C5.7.5.7 5.5.7 11.8c0 5 3.2 9.2 7.7 10.7.6.1.8-.2.8-.6v-2c-3.2.7-3.8-1.4-3.8-1.4-.5-1.3-1.3-1.6-1.3-1.6-1-.7.1-.7.1-.7 1.1.1 1.7 1.2 1.7 1.2 1 1.7 2.7 1.2 3.3.9.1-.7.4-1.2.7-1.5-2.5-.3-5.2-1.3-5.2-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.2 1.2 1-.3 2-.4 3-.4s2 .1 3 .4c2.2-1.5 3.2-1.2 3.2-1.2.6 1.6.2 2.8.1 3.1.7.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.2 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6 4.5-1.5 7.7-5.7 7.7-10.7C23.3 5.5 18.3.5 12 .5Z" />
      </svg>
    );
  }
  // Generic lock for enterprise SSO / SAML / custom OIDC.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
};
